import clsx from "clsx";
import styles from "./CompaniesDropdown.module.scss";
import { Typography } from "@/frontend/shared/ui/Typography";
import { Dispatch, RefObject, useEffect, useMemo, useRef } from "react";
import { Loader } from "@/frontend/shared/ui/Loader/Loader";
import { Logo } from "@/frontend/shared/ui/Logo";

export type TFormattedSearchedCompanies = {
  id: number;
  name: string;
  headline: string | null;
  logoUrl: string | null;
  linkedInUrl?: string;
};

type TCompaniesDropdown = {
  className?: string;
  inputValue: string;
  setInputValue: (value: string) => void;
  options: TFormattedSearchedCompanies[];
  isOpen: boolean;
  onClose: () => void;
  isCompaniesLoading?: boolean;
  companiesContainerRef?: RefObject<HTMLDivElement>;
  setCompetitorsData: Dispatch<React.SetStateAction<TFormattedSearchedCompanies[]>>;
  loadMore?: () => void;
};

export function CompaniesDropdown({
  className,
  inputValue,
  options,
  isOpen,
  onClose,
  isCompaniesLoading,
  companiesContainerRef,
  setCompetitorsData,
  loadMore
}: TCompaniesDropdown) {
  const loadMoreRef = useRef(null);

  function addCompanyToCompetitors(company: TFormattedSearchedCompanies) {
    setCompetitorsData((prev) => [...prev, company]);
    onClose();
  }

  useEffect(() => {
    if (isCompaniesLoading) return;
    const intersectionObserver = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        loadMore?.();
      }
    });
    if (loadMoreRef.current) {
      intersectionObserver.observe(loadMoreRef.current);
    }
    return () => {
      intersectionObserver.disconnect();
    };
  }, [loadMoreRef, loadMore, isCompaniesLoading]);

  const content = useMemo(() => {
    if (options?.length === 0) {
      if (isCompaniesLoading)
        return (
          <div className={styles.loadingContainer}>
            <Loader />
          </div>
        );

      return (
        <div className={styles.option}>
          <Typography variant="label-4" color="greyscale600">
            No results
          </Typography>
        </div>
      );
    }

    return (
      <>
        {options?.map((company, index) => {
          return (
            <div key={company.id} className={styles.option} onClick={() => addCompanyToCompetitors(company)}>
              <Logo logo={company.logoUrl || ""} alt={company.name} />
              <div>
                <Typography variant="label-4" color="greyscale800" className={styles.name}>
                  {company.name}
                </Typography>
                <Typography variant="body-3" color="greyscale600" className={styles.headline}>
                  {company.headline}
                </Typography>
              </div>
            </div>
          );
        })}
        {isCompaniesLoading && (
          <div className={styles.loadingMoreContainer}>
            <Loader />
          </div>
        )}
        <div ref={loadMoreRef} id="loadMore"></div>
      </>
    );
  }, [inputValue, options, isCompaniesLoading]);

  return (
    <div className={clsx(styles.container, isOpen && styles.isOpen, className)} ref={companiesContainerRef}>
      {content}
    </div>
  );
}
